// 包裹（EP-008-S3 / GDD 03 §②-2 / ⑥-S12 / D3 / D5）。
//   用户可见名：老板 2026-09 拍板「空间」→「包裹」（P6-POLISH-ENG-001）。
//   代码标识符不改：`SpaceView` / `spaceTab` / `view: 'space'` / CSS class `.space-view` 仍写 space。
// 3 列网格 + 顶 tab「礼物 / 明信片」+ 完成度「拾起 X / 10」。
// 独立全屏滚动容器（不受 y 1520–1820 安全区限制，D5）。
// 空态占位（无「去出发」按钮，D3）。点开 ItemCard → ItemDetail；点开 PostcardListView 中某条 → PostcardDetail。
//
// 🆕 v2（P-POSTCARD-RECONCEIVE）：
//   - tab 从「收藏 / 图鉴」扩展为「礼物 / 明信片」。"图鉴"语义并入"礼物" tab（仍可切换）。
//   - 明信片 tab：PostcardListView（按月份折叠） + 选中后 PostcardDetail 弹层。
//   - 完成度仍展示礼物分母；明信片用单独的"未读"小红点驱动（见 StatusCard 右上角）。
//   - 当前激活 tab 写到 store.spaceTab（StatusCard 角标点击入口读 store 决定默认 tab）。
import { useEffect, useMemo, useState } from 'react';
import { useGameStore, type SpaceTab } from '../store';
import { GIFTS } from '../domain/gifts/giftDef';
import { completion, sortStacks } from '../domain/inventory/selectors';
import type { InventoryStackV1 } from '../save/types';
import { POSTCARD_NOTES } from '../runtime/postcardNotes';
import { ItemCard } from './ItemCard';
import { ItemDetail } from './ItemDetail';
import { PostcardCodexView } from './space/PostcardCodexView';
import { PostcardDetail } from './space/PostcardDetail';

interface SpaceViewProps {
  onClose: () => void;
}

type Tab = SpaceTab; // 'gifts' | 'postcards'
type GiftTab = 'collected' | 'codex';

export function SpaceView({ onClose }: SpaceViewProps) {
  const save = useGameStore((s) => s.save);
  // 🆕 v2：从 store 读 spaceTab（受 StatusCard 角标入口控制）；用户切换时同步回写。
  const spaceTab = useGameStore((s) => s.spaceTab);
  // 直接取 store 的 openSpace 方法（不通过 selector 包一层函数引用，避免 React 误判"快照变了"）
  const openSpace = useGameStore.getState().openSpace;
  // 🆕 STEP 5-D：同款取法拿 markPostcardRead（action 引用在 Zustand 里稳定，无需 selector 订阅）。
  //   用于列表项点击处**同步**标已读 —— 不再依赖 PostcardDetail 的 mount useEffect。
  const markPostcardRead = useGameStore.getState().markPostcardRead;
  const markGiftRead = useGameStore.getState().markGiftRead;
  const [tab, setTab] = useState<Tab>(spaceTab);

  // 跟 store.spaceTab 同步（响应 StatusCard 角标入口等外部触发）
  useEffect(() => {
    setTab(spaceTab);
  }, [spaceTab]);
  // 礼物子 tab 默认值（P6-POLISH-ENG-001）：
  //   有收藏 → 「收藏」（老行为不变）；
  //   一件都没有 → 落到「图鉴」，因为图鉴列出全部 enabled 母题（剪影），一张也不会少，
  //   「刚点开」的第一眼就是实实在在的 10 张礼物卡，而不是孤零零一行空文案。
  //   SpaceView 每次开包裹都重新挂载（MainView 只在 view==='space' 时渲染它），
  //   所以这个惰性初值每次进包裹都会重算 —— 之后拿到礼物再进来就自然是「收藏」了。
  const [giftTab, setGiftTab] = useState<GiftTab>(() =>
    (save?.inventory.length ?? 0) > 0 ? 'collected' : 'codex',
  );
  const [openGiftId, setOpenGiftId] = useState<string | null>(null);
  const [openPostcardId, setOpenPostcardId] = useState<string | null>(null);

  // save 非空才可进入空间（store 已守卫 openSpace 仅在 home/awaitingReveal）
  const inventory = save?.inventory ?? [];
  const mailbox = save?.mailbox ?? [];
  const { owned, denominator } = completion(inventory);
  const stackByGift = new Map<string, InventoryStackV1>(inventory.map((s) => [s.giftId, s]));

  // 礼物子 tab：收藏仅已获得、图鉴全部 enabled 母题
  const giftRenderIds =
    giftTab === 'codex'
      ? GIFTS.filter((g) => g.enabled).map((g) => g.id)
      : sortStacks(inventory).map((s) => s.giftId);

  // 🆕 v2 → STEP 5-E 精确化：未读明信片数 = mailbox 中 isRead === false 的条目数。
  //   历史：v1.0 曾用 `mailbox.length`（总数）作近似，注释写着"后续批次按 read flag 精确化"；
  //   该近似已由 design-strategist 在 STEP 6 主动撤回（原话：「那是我拍的，现在我撤回」）。
  //   现改为按 read flag 派生的真未读数，与 MainView L78 主页红点公式同源，也与本组件
  //   aria-label「未读明信片 N 封」名副其实（改前是"总数"却标"未读"，语义与实现不符）。
  const unreadPostcardCount = useMemo(() => {
    return mailbox.filter((p) => !p.isRead).length;
  }, [mailbox]);

  // 🆕 v2.1：未读礼物数 = inventory 中 isRead === false 的 stack 数量。
  // 新入库礼物由 upsertStacks 注入 isRead: false；点开 ItemDetail → markGiftRead → true。
  const unreadGiftCount = useMemo(() => {
    return inventory.filter((s) => !s.isRead).length;
  }, [inventory]);

  const openPostcardEntry = openPostcardId
    ? mailbox.find((e) => e.id === openPostcardId) ?? null
    : null;

  // 🆕 P6-POLISH-ENG-002（07 §H.6）：明信片完成度按**去重 noteId** 计数，
  //   不能用 mailbox.length —— 历史存档里同一 noteId 可能有重复条目，会让数字 > 12。
  const postcardOwnedUnique = useMemo(() => new Set(mailbox.map((p) => p.noteId)).size, [mailbox]);
  const postcardTotal = POSTCARD_NOTES.length; // 12
  const postcardCompletionText =
    postcardOwnedUnique >= postcardTotal ? '都集齐了' : `明信片 ${postcardOwnedUnique} / ${postcardTotal}`;

  return (
    <div className="space-view">
      <div className="space-view__top">
        <button type="button" className="space-view__back" onClick={onClose} aria-label="返回房间">
          返回
        </button>
        <div className="space-view__tabs" role="tablist" aria-label="包裹视图切换">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'gifts'}
            className={tab === 'gifts' ? 'is-active' : ''}
            onClick={() => {
              setTab('gifts');
              openSpace('gifts');
            }}
          >
            礼物
            {unreadGiftCount > 0 && (
              <span className="space-view__badge" aria-label={`未读礼物 ${unreadGiftCount} 件`}>
                {unreadGiftCount}
              </span>
            )}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'postcards'}
            className={tab === 'postcards' ? 'is-active' : ''}
            onClick={() => {
              setTab('postcards');
              openSpace('postcards');
            }}
          >
            明信片
            {unreadPostcardCount > 0 && (
              <span className="space-view__badge" aria-label={`未读明信片 ${unreadPostcardCount} 封`}>
                {unreadPostcardCount}
              </span>
            )}
          </button>
        </div>
        {/* aria-label 跟着当前 tab 走：礼物说"件"、明信片说"张"，不要混（07 §H.6）。 */}
        <span
          className="space-view__completion"
          aria-label={
            tab === 'gifts'
              ? `已收集 ${owned} 件，共 ${denominator} 件`
              : `已收集 ${postcardOwnedUnique} 张明信片，共 ${postcardTotal} 张`
          }
        >
          {/* 🆕 P6-POLISH-ENG-002（07 §D.2）：「拾起」→「带回」，与主页顶部五档文案同词，
              避免"主页说带回、空间说拾起"。数字本身不违反 B1（空间页允许数字）。 */}
          {tab === 'gifts' ? `带回 ${owned} / ${denominator}` : postcardCompletionText}
        </span>
      </div>

      {/* 礼物 tab：保留 v1 的"收藏 / 图鉴"子 tab 切换。
          🆕 P6-POLISH-ENG-001：子 tab 行**恒定渲染**（以前收藏为空时整行连同图鉴一起被藏掉，
          页面只剩一行空文案，看着像"没打开"）。现在无论有没有收藏都能看到/切到「图鉴」。 */}
      {tab === 'gifts' && (
        <>
          <div className="space-view__subtabs" role="tablist" aria-label="礼物视图切换">
            <button
              type="button"
              role="tab"
              aria-selected={giftTab === 'collected'}
              className={giftTab === 'collected' ? 'is-active' : ''}
              onClick={() => setGiftTab('collected')}
            >
              收藏
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={giftTab === 'codex'}
              className={giftTab === 'codex' ? 'is-active' : ''}
              onClick={() => setGiftTab('codex')}
            >
              图鉴
            </button>
          </div>
          {giftRenderIds.length === 0 ? (
            // 「收藏」为空时才出现的空态引导：讲清"为什么空"和"怎么才有"，不含阿拉伯数字（B1）。
            // 🆕 P6-POLISH-ENG-002 §G.6.1：旧文案「它旅行回来的时候，会带一件给你」是**破承诺**——
            //   空手机制已获批准（名义 81% / 实际受连续空手禁止压到 ~45%），狗回来了可能没带，那句仍说"会带"会穿帮。改不承诺"下次一定有"。
            <div className="space-view__empty">
              收藏还空着。它带回来的，会慢慢摆在这里。可以先去「图鉴」看看都有什么。
            </div>
          ) : (
            <div className="space-view__grid">
              {giftRenderIds.map((giftId) => {
                const stack = stackByGift.get(giftId);
                return (
                  <ItemCard
                    key={giftId}
                    giftId={giftId}
                    count={stack?.count ?? 0}
                    obtained={!!stack}
                    // 🆕 §F：未读（首次获得且没在详情页打开过）的礼物打「新」标记。
                    //   图鉴子 tab 的未获得剪影卡 obtained=false → isNew 恒 false，不会误标。
                    isNew={!!stack && !stack.isRead}
                    onOpen={(id) => {
                      markGiftRead(id);
                      setOpenGiftId(id);
                    }}
                  />
                );
              })}
            </div>
          )}
          {openGiftId && (
            <ItemDetail
              giftId={openGiftId}
              stack={stackByGift.get(openGiftId) ?? undefined}
              onClose={() => setOpenGiftId(null)}
            />
          )}
        </>
      )}

      {/* 🆕 P6-POLISH-ENG-002（07 §H）：明信片 tab 从「按月分组的文字列表」换成「4 组 × 3 张图鉴网格」。
          点击表达契约不变 —— 仍在点击处**同步** markPostcardRead，再交给父级开详情。 */}
      {tab === 'postcards' && (
        <PostcardCodexView
          mailbox={mailbox}
          selectedId={openPostcardId}
          onSelect={(id) => {
            // 🆕 STEP 5-D：点击即**同步**标已读。
            //   顺序固定：① 先 markPostcardRead(id) 落盘 → ② 再 setOpenPostcardId(id) 开详情，
            //   保证详情渲染时拿到的已是 isRead=true 的新 entry（无中间态闪烁）。
            //   移到点击处的原因：挂在 PostcardDetail 的 mount useEffect 上依赖 React effect 调度，
            //   真浏览器（StrictMode 双挂载 / 列表重渲染 / entry 复用）下时序不可靠；
            //   同步调用时序确定，与渲染状态解耦。
            //   幂等由 store 保证（markPostcardRead 内部 `if (isRead) return`），重复点同一条只写一次。
            markPostcardRead(id);
            setOpenPostcardId(id);
          }}
        />
      )}

      {openPostcardEntry && (
        <PostcardDetail
          entry={openPostcardEntry}
          onClose={() => setOpenPostcardId(null)}
        />
      )}
    </div>
  );
}
